import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit'

import { LeadNote } from '@user/components/Notes/typings'
import { StoreData } from '../reducers/typings'

import {
  createLeadNote,
  deleteLeadNote,
  getLeadNotes,
  getLeadNoteById,
  updateLeadNote,
} from './api'

const leadNotesAdapter = createEntityAdapter<LeadNote>()

type LeadNotesState = {
  count: number
  isLoading: boolean
  leadId: number | null
  listIsStale: boolean
  notes: EntityState<LeadNote>
  page: number
  skip: number
  take: number
}

export const initialState: LeadNotesState = {
  count: 0,
  isLoading: false,
  leadId: null,
  listIsStale: false,
  page: 1,
  notes: leadNotesAdapter.getInitialState(),
  skip: 0,
  take: 0,
}

export const leadNoteSlice = createSlice({
  name: 'leadNotes',
  initialState,
  reducers: {
    setPage: (state: LeadNotesState, action: PayloadAction<number>) => {
      state.page = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLeadNotes.pending, (state) => {
      leadNotesAdapter.removeAll(state.notes)
      state.isLoading = true
    }),
      builder.addCase(getLeadNotes.fulfilled, (state, { payload }) => {
        leadNotesAdapter.addMany(state.notes, payload.items || [])
        state.count = payload.count
        state.leadId = payload.leadId
        state.listIsStale = false
        state.skip = payload.skip
        state.take = payload.take
        state.isLoading = false
      }),
      builder.addCase(getLeadNoteById.pending, (state) => {
        state.isLoading = true
      }),
      builder.addCase(getLeadNoteById.fulfilled, (state, { payload }) => {
        leadNotesAdapter.upsertOne(state.notes, payload)
        state.isLoading = false
      }),
      builder.addCase(deleteLeadNote.pending, (state) => {
        state.isLoading = true
      }),
      builder.addCase(deleteLeadNote.fulfilled, (state, { payload }) => {
        leadNotesAdapter.removeOne(state.notes, payload)
        state.count = state.count - 1
        state.isLoading = false
        state.listIsStale = true
      }),
      builder.addCase(createLeadNote.pending, (state) => {
        state.isLoading = true
      }),
      builder.addCase(createLeadNote.fulfilled, (state, { payload }) => {
        leadNotesAdapter.addOne(state.notes, payload)
        state.count = state.count + 1
        state.isLoading = false
        state.listIsStale = true
      }),
      builder.addCase(updateLeadNote.pending, (state) => {
        state.isLoading = true
      }),
      builder.addCase(updateLeadNote.fulfilled, (state, { payload }) => {
        leadNotesAdapter.upsertOne(state.notes, payload)
        state.isLoading = false
      })
  },
})

export default leadNoteSlice.reducer

// SELECTORS
export const leadNoteSelectors = leadNotesAdapter.getSelectors<StoreData>(
  (state) => state.leadNotes.notes,
)

export const selectLeadNoteSlice = (state: StoreData) => {
  return state.leadNotes
}

export const { selectById: selectLeadNoteById, selectAll: selectAllLeadNotes } =
  leadNoteSelectors

export const { setPage } = leadNoteSlice.actions
